import type { ReactNode } from "react";
import {
  closestCenter,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DndContextProps,
  type DropAnimation,
  type UniqueIdentifier,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";

import { sortableAccessibility } from "./sortableAccessibility";

/**
 * The one drag setup every vertical sortable list in the app shares (the
 * builder's question list and `SortableList`: answer options, grid rows):
 * pointer and keyboard sensors, the closest-centre collision, the vertical
 * axis, and what a screen reader hears. `ids` are the list's items in order.
 * Spread it on the list's `DndContext`.
 */
export function useSortableDrag(
  ids: ReadonlyArray<UniqueIdentifier>,
): Pick<
  DndContextProps,
  "sensors" | "collisionDetection" | "modifiers" | "accessibility"
> {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  return {
    sensors,
    collisionDetection: closestCenter,
    modifiers: [restrictToVerticalAxis],
    accessibility: sortableAccessibility(ids),
  };
}

/**
 * A sortable list's drag overlay. dnd-kit measures an overlay by its one
 * child and collides with that rect, so a compact preview (a one-line chip
 * standing for a tall card) moved the drop target: picked up and dropped
 * without moving, a question swapped above the one before it, because the
 * chip's centre sat nearer that question's centre than its own. The preview
 * sits in a box that fills the overlay, which dnd-kit sizes as the dragged
 * item, so the overlay collides as the item itself, as a list without an
 * overlay does.
 */
export function SortableDragOverlay(props: {
  dropAnimation?: DropAnimation | null;
  children: ReactNode;
}) {
  const { dropAnimation, children } = props;
  return (
    <DragOverlay dropAnimation={dropAnimation}>
      {children ? (
        <div className="h-full w-full" data-testid="sortable-drag-overlay-item">
          {children}
        </div>
      ) : null}
    </DragOverlay>
  );
}
